import { $settings } from '@/provider/settings';
import type { AccountInfo } from '@azure/msal-browser';
import {
  token,
  setProfile,
  setToken,
  setTokenExpiry,
  getTokenExpiry,
  isExpired,
  clearAuth,
} from '@/services/auth/utils/authUtils';
import type { AuthContext } from '@/services/auth/types';
import { useMsalWrapper } from '@/services/auth/composables/useMsalWrapper';
import { LoginInfoError } from '@/utils/errors/LoginInfoError';

const { getAccessTokenByScopes, msalLogIn, msalLogOut } = useMsalWrapper();

const isAuthExpired = () => isExpired(getTokenExpiry());

const authLogout = async () => msalLogOut();

const setProfileInformation = (account: AccountInfo) => {
  const { homeAccountId: accountId, username, name: fullName } = account;
  setProfile({ accountId, username, fullName });
};

const handleTokenInformation = async () => {
  const scope = `${$settings.authentication.audience}/webX`;
  const authResult = await getAccessTokenByScopes([scope]);

  if (authResult) {
    const { accessToken, expiresOn } = authResult;
    setTokenExpiry(expiresOn);
    setToken(accessToken);
    return accessToken;
  }

  clearAuth();

  throw new LoginInfoError(
    'Failed to acquire token, user may need to login again',
    'token_acquisition_failed',
  );
};

const getGraphToken = async () => {
  try {
    const accessToken = await getAccessTokenByScopes(['Calendars.ReadWrite']);
    return accessToken;
  } catch {
    const accessToken = await getAccessTokenByScopes(['Calendars.Read']);
    return accessToken;
  }
};

const getPowerBIToken = async () => {
  const accessToken = await getAccessTokenByScopes([
    'https://analysis.windows.net/powerbi/api/.default',
  ]);
  return accessToken;
};

const authLogin = async () => {
  const account = await msalLogIn();
  if (account) {
    setProfileInformation(account);
    await handleTokenInformation();
  } else {
    throw new LoginInfoError(
      'No account returned after login',
      'login_failed',
    );
  }
};

const getAuthToken = async (): Promise<string | null> => {
  const accessToken = token.value;
  if (!accessToken) {
    return null;
  }

  if (!isAuthExpired()) {
    return accessToken;
  }

  return handleTokenInformation();
};

export const useAuthDefault = (): AuthContext => ({
    authLogout,
    authLogin,
    getAuthToken,
    getGraphToken,
    getPowerBIToken,
  });
